/* Hipster gift claims. Pricing and discount allocation always come from Shopify. */
(function (root) {
  'use strict';
  const MARK = '_hipster_gift_rule';
  // Offer rules. Shopify's discounts still decide what is actually free; these only decide when to prompt and what to offer.
  //   buy / get   pieces bought per free pieces earned (buy 2, get 1).
  //   cap         most free pieces per order for this offer.
  //   choice      'same': the free piece is the product the shopper bought, counted product by product, no menu.
  //               'any':  the shopper picks any product in this offer's pool from a menu (scent ranges).
  //   discount    optional text/regex matched against the Shopify discount title; default "Buy <buy> … get <get> … free".
  // See HOW-TO-CHANGE-OFFERS.md before editing.
  const RULES = [
    { id: 'sunscreen', buy: 1, get: 1, cap: 2, choice: 'same', products: [14712661311853,14712662786413,14712663179629] },
    { id: 'rollon', buy: 1, get: 1, cap: 2, choice: 'any', scents: true, products: [14712661213549,14712661246317,14712662884717,14712662917485] },
    { id: 'antiaging', buy: 1, get: 1, cap: 2, choice: 'same', products: [14712661180781] },
    { id: 'bodymilk', buy: 1, get: 1, cap: 2, choice: 'any', scents: true, collections: ['body-milk'] },
    // These overlapping buy-two campaigns share a shopper-facing pool/cap.
    // Otherwise one main-collection item plus one brand-only item never qualifies.
    { id: 'buy2-main', aliases: ['buy2-brands'], buy: 2, get: 1, cap: 2, choice: 'same', collections: ['main-collection','ventamor','skykur','sucair','relat','keranty','others'] }
  ];
  function gets(rule) { return rule.get || 1; }
  function discountPattern(rule) {
    if (rule?.discount) return rule.discount instanceof RegExp ? rule.discount : new RegExp(rule.discount, 'i');
    const buy = rule ? rule.buy : '[12]', get = rule ? gets(rule) : '[12]';
    return new RegExp('buy\\s*[_ ]?\\s*' + buy + '.*get\\s*[_ ]?\\s*' + get + '.*free', 'i');
  }
  function validCart(cart) {
    return !!cart && Array.isArray(cart.items) && Number.isInteger(cart.item_count) &&
      Number.isFinite(cart.total_price) && cart.items.every(i => Number.isInteger(i.quantity) && typeof i.key === 'string');
  }
  function finalPrice(item) { return item.final_line_price ?? item.line_price; }
  function freeUnits(item, rule) {
    const price = item.original_price || item.price;
    if (!price) return 0;
    const allocations = item.line_level_discount_allocations || [];
    if (finalPrice(item) === 0 && (!rule || !allocations.length)) return item.quantity;
    // Attribution matters: a roll-on can be the free piece of the wider buy-two
    // promotion, rather than the roll-on's own buy-one promotion.
    const pattern = discountPattern(rule);
    const amount = allocations.reduce((sum, a) => pattern.test(a.discount_application?.title || '') ? sum + a.amount : sum, 0);
    return Math.min(item.quantity, Math.floor(amount / price));
  }
  function ruleId(item) {
    const id = item.properties?.[MARK];
    return RULES.find(r => r.id === id || r.aliases?.includes(id))?.id || id;
  }
  function productEarnings(items, rule) {
    // Free pieces each product earns on its own, and the managed gifts of it already in the bag.
    const earnings = new Map();
    for (const id of new Set(items.map(i => i.product_id))) {
      const rows = items.filter(i => i.product_id === id);
      const claimed = rows.filter(i => ruleId(i) === rule.id).reduce((n,i) => n + i.quantity, 0);
      const manualPaid = Math.max(0, rows.reduce((n,i) => n + freeUnits(i), 0) - claimed);
      const paid = Math.max(0, rows.filter(i => !i.properties?.[MARK]).reduce((n,i) => n + i.quantity, 0) - manualPaid);
      earnings.set(id, { earned: Math.floor(paid / rule.buy) * gets(rule), claimed });
    }
    return earnings;
  }
  function groupState(cart, rule, assigned) {
    const items = cart.items.filter(i => !i.selling_plan_allocation && assigned.get(i.product_id) === rule.id);
    const managed = cart.items.filter(i => ruleId(i) === rule.id);
    const managedQuantity = managed.reduce((n,i) => n + i.quantity, 0);
    const base = items.filter(i => !i.properties?.[MARK]);
    const free = items.reduce((n,i) => n + freeUnits(i, rule), 0);
    const manualPaid = Math.max(0, items.reduce((n,i) => n + freeUnits(i), 0) - managedQuantity);
    const paid = Math.max(0, base.reduce((n,i) => n + i.quantity, 0) - manualPaid);
    const outsideFree = rule.buy === 2 ? cart.items.filter(i => assigned.get(i.product_id) !== rule.id).reduce((n,i) => n + (i.line_level_discount_allocations?.length ? freeUnits(i, rule) : 0), 0) : 0;
    const manual = Math.max(0, free - managedQuantity) + outsideFree;
    // 'same' offers qualify product by product: two different products never combine into one gift.
    const earned = rule.choice === 'same' ? [...productEarnings(items, rule).values()].reduce((n,e) => n + e.earned, 0) : Math.floor(paid / rule.buy) * gets(rule);
    const allowed = Math.max(0, Math.min(rule.cap, earned) - manual);
    return { base, managed, managedQuantity, free, allowed, remaining: Math.max(0, allowed - managedQuantity) };
  }
  function fingerprint(cart) {
    // Aggregate discount-split rows and ignore Shopify's volatile keys/prices.
    const quantities = new Map();
    for (const item of cart.items.filter(i => !i.properties?.[MARK])) {
      const properties = Object.fromEntries(Object.entries(item.properties || {}).sort(([a],[b]) => a.localeCompare(b)));
      const identity = JSON.stringify([item.variant_id || item.id, properties, item.selling_plan_allocation?.selling_plan?.id || null]);
      quantities.set(identity, (quantities.get(identity) || 0) + item.quantity);
    }
    return JSON.stringify([...quantities].sort(([a],[b]) => a.localeCompare(b)));
  }
  function decisionKey(cart, rule, assigned) {
    return fingerprint({items: groupState(cart, rule, assigned).base});
  }
  function pruneDecisions(decisions, cart, rules, assigned) {
    for (const [id, key] of decisions) {
      const rule = rules.find(r => r.id === id);
      if (!rule || !groupState(cart, rule, assigned).allowed || key !== decisionKey(cart, rule, assigned)) decisions.delete(id);
    }
  }
  function giftOptions(cart, rule, assigned, variants, unavailable) {
    const key = decisionKey(cart, rule, assigned);
    const usable = v => v.available === true && assigned.get(v.product_id) === rule.id && unavailable.get(v.id) !== key;
    if (rule.choice !== 'same') return [...variants.values()].filter(usable);
    // Only products already in the bag, while they still have an unclaimed free piece, in bag order.
    const items = cart.items.filter(i => !i.selling_plan_allocation && assigned.get(i.product_id) === rule.id);
    const earnings = productEarnings(items, rule);
    const options = [];
    for (const item of groupState(cart, rule, assigned).base) {
      const variant = variants.get(item.variant_id || item.id), earning = earnings.get(item.product_id);
      if (variant && earning && earning.earned > earning.claimed && !options.includes(variant) && usable(variant)) options.push(variant);
    }
    return options;
  }
  function removals(cart, rules, assigned) {
    const updates = {};
    for (const item of cart.items) {
      if (item.properties?.[MARK] && !rules.some(r => r.id === ruleId(item))) updates[item.key] = 0;
    }
    for (const rule of rules) {
      const group = groupState(cart, rule, assigned);
      // Remove gifts that lost eligibility or are no longer covered by a free-unit allocation.
      let keep = Math.min(group.allowed, group.free);
      for (const item of group.managed) {
        const quantity = Math.min(keep, item.quantity);
        keep -= quantity;
        if (quantity !== item.quantity) updates[item.key] = quantity;
      }
    }
    return updates;
  }
  function logicalUpdates(cart, updates) {
    const mapped = {};
    for (const [key,quantity] of Object.entries(updates)) {
      const item = cart.items.find(i => i.key === key);
      // Shopify can put the free price on the original pieces and charge the
      // tagged claims. Keep controls attached to paid/gift intent, including
      // when two original pieces correspond to separate one-piece claims.
      const partners = item ? cart.items.filter(i => i.key !== item.key && i.id === item.id &&
        !i.selling_plan_allocation && !item.selling_plan_allocation &&
        ((finalPrice(item) === 0 && !item.properties?.[MARK] && i.properties?.[MARK] && finalPrice(i) > 0) ||
         (finalPrice(item) > 0 && item.properties?.[MARK] && !i.properties?.[MARK] && finalPrice(i) === 0))) : [];
      if (!partners.length) { mapped[key] = quantity; continue; }
      let delta = quantity - item.quantity;
      for (const target of partners) {
        const current = mapped[target.key] ?? target.quantity;
        const change = delta >= 0 ? delta : Math.max(delta, -current);
        mapped[target.key] = current + change;
        delta -= change;
        if (!delta) break;
      }
    }
    return mapped;
  }
  const api = { RULES, MARK, validCart, groupState, fingerprint, removals, freeUnits, logicalUpdates, decisionKey, pruneDecisions, giftOptions, productEarnings, discountPattern };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document) return;

  function boot() {
    if (root.HipsterGifts) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const route = root.Shopify?.routes?.root || '/';
    const ar = document.documentElement.lang.startsWith('ar');
    const words = ar ? {
      title: 'يمكنك الحصول على هدية مجانية', choose: 'اختر هديتك', chooseScent: 'اختر الرائحة — نفس الرائحة أو رائحة أخرى', piece: 'قطعتك المجانية:', claim: 'أضف الهدية المجانية', no: 'لا، شكرًا',
      error: 'تعذر تحديث السلة. يرجى المحاولة مرة أخرى.', unavailable: 'تعذر إضافة هذه الهدية مجانًا. اختر هدية أخرى إن توفرت.',
      added: 'تمت إضافة هديتك المجانية.', free: 'هدية مجانية', offer: 'تم تطبيق العرض', checking: 'جارٍ التحقق من الهدية…', updating: 'جارٍ تحديث السلة…', decide: 'اختر إضافة الهدية أو لا، شكرًا قبل إتمام الطلب.'
    } : {
      title: 'You’ve unlocked a free gift', choose: 'Choose your gift', chooseScent: 'Pick a scent — the same one or another', piece: 'Your free piece:', claim: 'Claim free gift', no: 'No thanks',
      error: 'We couldn’t update your bag. Please try again.', unavailable: 'We couldn’t add this gift for free. Please choose another available gift.',
      added: 'Your free gift has been added.', free: 'Free gift', offer: 'Offer applied', checking: 'Checking your gift…', updating: 'Updating bag…', decide: 'Please claim your free gift or choose No thanks before checkout.'
    };
    let queue = Promise.resolve(), busy = false, pending = 0, refreshPromise, catalogPromise, lastCart;
    const declined = new Map();
    const storageKey = 'hipster-gift-decisions-v2';
    try { for (const [id,key] of JSON.parse(sessionStorage.getItem(storageKey) || '[]')) declined.set(id,key); } catch (_) {}
    const assigned = new Map(), variants = new Map(), unavailable = new Map();
    const digits = n => ar ? String(n).replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]) : String(n);
    const offerNames = ar ? {sunscreen:'واقي الشمس', rollon:'مزيل العرق', antiaging:'سيروم مقاومة التجاعيد', bodymilk:'لوشن الجسم'} : {sunscreen:'Sunscreen', rollon:'Roll-on', antiaging:'Antiaging serum', bodymilk:'Body milk'};
    // "Buy 2, get 1 free" follows the rule, so changing buy/get in RULES changes the wording too.
    function offerName(rule) {
      const deal = ar ? 'اشتري ' + digits(rule.buy) + ' واحصل على ' + digits(gets(rule)) + ' مجانًا' : 'Buy ' + rule.buy + ', get ' + gets(rule) + ' free';
      return offerNames[rule.id] ? offerNames[rule.id] + ' · ' + deal : deal;
    }
    function saveDecisions() {
      try { sessionStorage.setItem(storageKey, JSON.stringify([...declined])); } catch (_) {}
    }
    function syncDecisions(cart) {
      pruneDecisions(declined, cart, RULES, assigned);
      saveDecisions();
    }
    function decline(rule, cart) {
      declined.set(rule.id, decisionKey(cart, rule, assigned));
      saveDecisions();
    }
    function message(text, isError = false) {
      let el = drawer.querySelector('[data-gift-status]');
      if (!el) {
        el = document.createElement('p'); el.dataset.giftStatus = ''; el.className = 'hipster-gift-status';
        el.setAttribute('role','status'); el.setAttribute('aria-live','polite');
        (drawer.querySelector('.cart-drawer-body') || drawer).prepend(el);
      }
      el.textContent = text; el.style.color = isError ? '#a32121' : '';
    }
    function setBusy(value) {
      busy = value;
      drawer.setAttribute('aria-busy', String(value));
      let progress = drawer.querySelector('[data-gift-progress]');
      if (value && !progress) {
        progress = document.createElement('span'); progress.dataset.giftProgress = '';
        progress.setAttribute('role','status'); progress.setAttribute('aria-live','polite');
        progress.style.cssText = 'display:block;font-size:13px;line-height:1.4;color:#12694c;margin-top:4px';
        progress.textContent = words.updating;
        drawer.querySelector('.cart-drawer-header')?.append(progress);
      }
      if (!value) progress?.remove();
      document.querySelectorAll('[data-hipster-gift-claim], [data-hipster-gift-no]').forEach(el => {
        if (value && !el.disabled) { el.dataset.giftBusyDisabled = ''; el.disabled = true; }
        if (!value && el.hasAttribute('data-gift-busy-disabled')) { el.disabled = false; delete el.dataset.giftBusyDisabled; }
      });
    }
    async function request(path, body) {
      const response = await fetch(route + path, {
        cache: 'no-store', headers: { Accept:'application/json', ...(body ? {'Content-Type':'application/json'} : {}) },
        ...(body ? {method:'POST', body:JSON.stringify(body)} : {})
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400) { const error = new Error(result.description || words.error); error.status = result.status || response.status; throw error; }
      return result;
    }
    async function getCart() {
      const cart = await request('cart.js');
      if (!validCart(cart)) throw new Error(words.error);
      return cart;
    }
    // The offer catalog (which product belongs to which offer, and its pieces) is kept for the
    // session so the drawer does not wait on nine product requests every page. Stock is enforced
    // by Shopify when a piece is added, so a few minutes of staleness only affects the menu.
    const catalogKey = 'hipster-gift-catalog-v1', catalogTtl = 10 * 60 * 1000;
    function loadCatalog(entries) {
      assigned.clear(); variants.clear();
      for (const [id,ruleId] of entries.assigned) assigned.set(id, ruleId);
      for (const [id,v] of entries.variants) variants.set(id, v);
    }
    async function catalog() {
      if (!catalogPromise) catalogPromise = (async () => {
        const stamp = JSON.stringify(RULES);
        try {
          const cached = JSON.parse(sessionStorage.getItem(catalogKey) || 'null');
          if (cached?.stamp === stamp && Date.now() - cached.at < catalogTtl) { loadCatalog(cached); return; }
        } catch (_) {}
        const handles = [...new Set(RULES.flatMap(r => r.collections || [])), 'offers'];
        // One round trip: the collection lists and the full product list load together.
        const [all, ...lists] = await Promise.all(['products.json?limit=250', ...handles.map(h => 'collections/' + h + '/products.json?limit=250')].map(async path => {
          const json = await request(path);
          if (!Array.isArray(json.products)) throw new Error(words.error);
          return json.products;
        }));
        const data = new Map(handles.map((h,i) => [h,lists[i]]));
        const excluded = new Set(data.get('offers').map(p => p.id));
        const products = new Map(all.map(p => [p.id,p]));
        const entries = { stamp, at:Date.now(), assigned:[], variants:[] }, taken = new Set();
        for (const rule of RULES) {
          const ids = rule.products || (rule.collections || []).flatMap(h => data.get(h).map(p => p.id));
          for (const id of ids) {
            const p = products.get(id);
            if (!p || excluded.has(id) || taken.has(id)) continue;
            taken.add(id); entries.assigned.push([id, rule.id]);
            for (const v of p.variants) entries.variants.push([v.id, { id:v.id, available:v.available, product_id:id, handle:p.handle, name:p.title + (v.title === 'Default Title' ? '' : ' — ' + v.title), image:v.featured_image?.src || p.images?.[0]?.src || '' }]);
          }
        }
        loadCatalog(entries);
        try { sessionStorage.setItem(catalogKey, JSON.stringify(entries)); } catch (_) {}
      })().catch(error => { catalogPromise = undefined; throw error; });
      return catalogPromise;
    }
    function badges(cart) {
      for (const row of document.querySelectorAll('.cart-product')) {
        const item = cart.items.find(i => i.key === row.dataset.itemKey);
        if (!item) continue;
        row.querySelectorAll('.cart-product-badge').forEach(b => b.remove());
        const text = finalPrice(item) === 0 ? words.free : item.line_level_discount_allocations?.length ? words.offer : '';
        if (!text) continue;
        const badge = document.createElement('span');
        badge.className = 'cart-product-badge ' + (finalPrice(item) === 0 ? 'cart-product-badge--free' : 'cart-product-badge--offer');
        badge.textContent = text;
        row.querySelector('.cart-product-title')?.after(badge);
      }
    }
    // Previews show a change before Shopify confirms it. Shopify's answer always replaces them:
    // render() clears every preview first, and a failed request restores the last drawn bag.
    function counts(n) { document.querySelectorAll('.cart-count').forEach(el => { el.textContent = String(n); }); }
    function dimTotals(on) { drawer.querySelectorAll('.cart-totals').forEach(el => { el.style.opacity = on ? '0.45' : ''; el.style.transition = 'opacity .15s'; }); }
    let unconfirmed = {};
    function clearPreview() {
      unconfirmed = {};
      document.querySelectorAll('[data-hipster-provisional]').forEach(row => row.remove());
      document.querySelectorAll('.cart-product[data-hipster-hidden]').forEach(row => { row.hidden = false; row.style.display = ''; delete row.dataset.hipsterHidden; });
      dimTotals(false);
      if (lastCart) counts(lastCart.item_count);
    }
    function previewUpdates(updates) {
      if (!lastCart) return;
      for (const [key,quantity] of Object.entries(updates)) {
        if (quantity > 0) continue;
        // Inline display beats any theme rule on .cart-product, unlike the hidden attribute alone.
        document.querySelectorAll('.cart-product[data-item-key]').forEach(row => { if (row.dataset.itemKey === key) { row.hidden = true; row.style.display = 'none'; row.dataset.hipsterHidden = ''; } });
      }
      counts(lastCart.items.reduce((n,i) => n + (updates[i.key] ?? i.quantity), 0));
      dimTotals(true);
    }
    // A gift row drawn from the row of the same product, or any row with its picture swapped.
    function previewGift(variant) {
      const rows = [...drawer.querySelectorAll('.cart-product[data-item-key]')].filter(r => !('hipsterHidden' in r.dataset));
      const same = rows.find(r => lastCart?.items.some(i => i.key === r.dataset.itemKey && (i.variant_id || i.id) === variant.id));
      const source = same || rows[0];
      if (!source) return;
      const row = source.cloneNode(true);
      row.removeAttribute('data-item-key'); row.dataset.hipsterProvisional = ''; row.style.opacity = '0.6'; row.setAttribute('aria-busy','true');
      row.querySelectorAll('.cart-product-badge, .cart-product-discounts, .cart-product-original-line-price, .cart-product-remove, .cart-product-variant, .cart-product-option, .error').forEach(el => el.remove());
      const title = row.querySelector('.cart-product-title');
      if (title) { title.textContent = variant.name; const badge = document.createElement('span'); badge.className = 'cart-product-badge cart-product-badge--free'; badge.textContent = words.free; title.after(badge); }
      if (!same) {
        const img = row.querySelector('.cart-product-thumbnail img');
        if (img && variant.image) { img.src = variant.image; img.removeAttribute('srcset'); img.removeAttribute('sizes'); }
        else row.querySelector('.cart-product-thumbnail')?.remove();
      }
      const input = row.querySelector('.product-quantity-picker-input');
      if (input) { input.value = '1'; input.disabled = true; input.removeAttribute('name'); }
      const price = row.querySelector('.cart-product-line-price-final');
      if (price) price.textContent = '';
      source.after(row);
      counts((lastCart?.item_count ?? 0) + 1);
      dimTotals(true);
    }
    function sectionNames() {
      const mainSection = document.querySelector('.section-main-cart-container')?.closest('.shopify-section')?.id.replace('shopify-section-','');
      return ['cart-drawer', ...(mainSection ? [mainSection] : [])];
    }
    async function updateCart(updates) {
      const cart = await request('cart/update.js', {updates, sections:sectionNames(), sections_url:root.location.pathname});
      if (!validCart(cart)) throw new Error(words.error);
      syncDecisions(cart);
      return cart;
    }
    // True when the rows on screen (line key + quantity) are exactly the cart's lines.
    function rowsMatch(container, cart) {
      const rows = [...container.querySelectorAll('.cart-product[data-item-key]')].filter(r => !('hipsterHidden' in r.dataset)).map(r => r.dataset.itemKey + ':' + (r.querySelector('.product-quantity-picker-input')?.value ?? ''));
      const items = cart.items.map(i => i.key + ':' + i.quantity);
      return rows.length === items.length && rows.sort().join('\n') === items.sort().join('\n');
    }
    function onScreen(cart) {
      return [drawer, document.querySelector('.section-main-cart-container')].filter(Boolean).every(c => rowsMatch(c, cart));
    }
    async function render(cart) {
      if (!validCart(cart)) throw new Error(words.error);
      clearPreview();
      const main = document.querySelector('.section-main-cart-container');
      const mainSection = main?.closest('.shopify-section')?.id.replace('shopify-section-','');
      const names = ['cart-drawer', ...(mainSection ? [mainSection] : [])];
      // A cart change brings its own HTML (sections) and is always drawn. A plain re-read keeps
      // the HTML already on screen when it shows this cart: the theme's add-to-cart render, the
      // page's own render, or ours. That skips the slowest request on every drawer open.
      if (cart.sections || !onScreen(cart)) {
        const sections = names.every(name => cart.sections?.[name]) ? cart.sections : await request('?sections=' + names.map(encodeURIComponent).join(','));
        const parsed = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
        const inner = parsed.querySelector('.cart-drawer-inner');
        if (!inner) throw new Error(words.error);
        const note = drawer.querySelector('textarea[name="note"]');
        const noteValue = note?.value;
        drawer.querySelector('.cart-drawer-inner').replaceWith(inner);
        if (noteValue !== undefined && inner.querySelector('textarea[name="note"]')) inner.querySelector('textarea[name="note"]').value = noteValue;
        if (mainSection) {
          const nextMain = new DOMParser().parseFromString(sections[mainSection] || '', 'text/html').querySelector('.section-main-cart-container');
          if (!nextMain) throw new Error(words.error);
          main.replaceWith(nextMain);
        }
        document.querySelectorAll('.lazy-loading-image').forEach(el => { if (typeof root.lazyloading === 'function') root.lazyloading(el); });
      }
      counts(cart.item_count);
      lastCart = cart; badges(cart); if (busy) setBusy(true);
    }
    let promptSig;
    function prompt(cart) {
      syncDecisions(cart);
      // Same bag and same decisions as the prompt already on screen: leave it alone.
      const sig = JSON.stringify([cart.items.map(i => [i.key, i.quantity, finalPrice(i), i.properties || null]), cart.total_price, [...declined], [...unavailable]]);
      if (drawer.querySelector('[data-hipster-gift-prompt]') && promptSig === sig) return;
      promptSig = sig;
      drawer.querySelectorAll('[data-hipster-gift-prompt]').forEach(el => el.remove());
      for (const rule of RULES) {
        const group = groupState(cart, rule, assigned);
        const key = decisionKey(cart, rule, assigned);
        if (!group.remaining || declined.get(rule.id) === key) continue;
        const options = giftOptions(cart, rule, assigned, variants, unavailable);
        if (!options.length) continue;
        // Menu choices: the scent already in the bag first, then the others. 'same' options keep bag order.
        if (rule.choice !== 'same') options.sort((a,b) => Number(group.base.some(i => i.id === b.id)) - Number(group.base.some(i => i.id === a.id)) || a.name.localeCompare(b.name));
        const box = document.createElement('section'); box.dataset.hipsterGiftPrompt = ''; box.className = 'hipster-gift-prompt';
        const title = document.createElement('h4'); title.textContent = words.title;
        const offer = document.createElement('p'); offer.textContent = offerName(rule); offer.style.cssText = 'font-size:13px;line-height:1.4;margin:0 0 10px';
        box.append(title, offer);
        let chosen = () => options[0].id;
        if (options.length > 1) {
          const label = document.createElement('label'); label.textContent = rule.scents ? words.chooseScent : words.choose; label.htmlFor = 'gift-choice-' + rule.id;
          const select = document.createElement('select'); select.id = label.htmlFor;
          for (const variant of options) { const option = document.createElement('option'); option.value = variant.id; option.textContent = variant.name; select.append(option); }
          chosen = () => Number(select.value);
          box.append(label, select);
        } else {
          // One possible gift: name it instead of showing a one-item menu.
          const piece = document.createElement('p'); piece.dataset.hipsterGiftPiece = ''; piece.textContent = words.piece + ' ' + options[0].name;
          piece.style.cssText = 'font-size:15px;line-height:1.4;font-weight:600;margin:0 0 12px';
          box.append(piece);
        }
        const yes = document.createElement('button'); yes.type = 'button'; yes.textContent = words.claim; yes.dataset.hipsterGiftClaim = '';
        const no = document.createElement('button'); no.type = 'button'; no.textContent = words.no; no.dataset.hipsterGiftNo = '';
        yes.addEventListener('click', () => run(() => claim(rule, chosen())));
        no.addEventListener('click', () => { decline(rule, cart); drawer.querySelector('[data-gift-status]')?.remove(); prompt(cart); });
        box.append(yes, no);
        drawer.querySelector('.cart-drawer-body')?.prepend(box);
        break; // One decision at a time, then offer the next eligible promotion.
      }
    }
    async function reconcile(cart) {
      // Removing one offer can change Shopify's allocation for another offer.
      // Re-read each returned cart until every remaining managed gift is covered.
      for (let attempt = 0; attempt <= RULES.length; attempt++) {
        const updates = removals(cart, RULES, assigned);
        if (!Object.keys(updates).length) return cart;
        cart = await updateCart(updates);
      }
      throw new Error(words.error);
    }
    async function refresh(cart) {
      // Re-opening an unchanged bag: show the prompt at once, then confirm against the store.
      if (!validCart(cart) && lastCart && assigned.size && onScreen(lastCart)) prompt(lastCart);
      const [, current] = await Promise.all([catalog(), validCart(cart) ? cart : getCart()]);
      cart = await reconcile(current);
      await render(cart); prompt(cart);
      return cart;
    }
    function scheduleRefresh() {
      if (!refreshPromise) refreshPromise = run(refresh).finally(() => { refreshPromise = undefined; });
      return refreshPromise;
    }
    async function claim(rule, id) {
      await catalog();
      // The bag the prompt was built from is current when it is still what is on screen.
      const before = await reconcile(lastCart && onScreen(lastCart) ? lastCart : await getCart());
      const variant = variants.get(id);
      if (!variant?.available || assigned.get(variant.product_id) !== rule.id || !groupState(before,rule,assigned).remaining) {
        await render(before); prompt(before); return;
      }
      message(words.checking); previewGift(variant);
      // Unique property lets rollback remove only this request, even when Shopify changes line keys.
      const claimId = root.crypto.randomUUID();
      let after;
      try {
        // Shopify refuses a piece that sold out since the menu loaded (422, handled below), and
        // returns the drawer HTML with the add, so the new bag needs one more read for its totals.
        const response = await request('cart/add.js',{items:[{id,quantity:1,properties:{[MARK]:rule.id,_hipster_gift_claim:claimId}}], sections:sectionNames(), sections_url:root.location.pathname});
        after = await getCart();
        if (response.sections) after.sections = response.sections;
        const added = after.items.filter(i => i.properties?._hipster_gift_claim === claimId);
        const group = groupState(after,rule,assigned);
        if (!added.length || after.total_price > before.total_price || group.free < group.managedQuantity || Object.keys(removals(after, RULES, assigned)).length) throw new Error(words.unavailable);
      } catch (error) {
        // An add request can succeed despite a lost response. Re-read before rolling back.
        const current = await getCart();
        const updates = Object.fromEntries(current.items.filter(i => i.properties?._hipster_gift_claim === claimId).map(i => [i.key,0]));
        const restored = Object.keys(updates).length ? await updateCart(updates) : current;
        // A failed variant is not a declined offer. Keep the other choices usable.
        if (error.status === 422 || error.message === words.unavailable) unavailable.set(id, decisionKey(restored, rule, assigned));
        await render(restored); prompt(restored); message(words.unavailable,true); return;
      }
      await render(after); prompt(after); message(words.added);
    }
    function run(work) {
      pending++; setBusy(true);
      const next = queue.then(async () => {
        try { return await work(); }
        catch (error) { message(words.error,true); console.error('[hipster-cart]',error); }
        finally { pending--; setBusy(pending > 0); }
      });
      queue = next; return next;
    }
    // Replace the legacy update path, which treated error JSON as a cart and rendered undefined.
    // Changes made while a request is in flight are merged into the next one (last value per
    // row wins), so rapid +/− clicks cost one request and never lock the drawer.
    let pendingUpdates = null, pendingRun;
    root.ChangeCartJson = updates => {
      Object.assign(unconfirmed, updates); previewUpdates(unconfirmed);
      if (pendingUpdates) { Object.assign(pendingUpdates, updates); return pendingRun; }
      pendingUpdates = { ...updates };
      pendingRun = run(async () => {
        const batch = pendingUpdates; pendingUpdates = null;
        try {
          await catalog();
          // The rows being changed come from the bag last drawn; re-read only if a key is unknown.
          const known = lastCart && Object.keys(batch).every(key => lastCart.items.some(i => i.key === key));
          const current = known ? lastCart : await getCart();
          const mapped = logicalUpdates(current, batch);
          for (const [key,quantity] of Object.entries(mapped)) {
            const item = current.items.find(i => i.key === key);
            if (item?.properties?.[MARK] && quantity < item.quantity) {
              const rule = RULES.find(r => r.id === ruleId(item));
              if (rule) decline(rule, current);
            }
          }
          const result = await updateCart(mapped);
          if (!validCart(result)) throw new Error(words.error);
          // More changes are waiting: they will draw the final bag, so skip drawing this one.
          if (pendingUpdates) { lastCart = result; return; }
          await refresh(result);
        } catch (error) {
          clearPreview();
          if (!pendingUpdates) await refresh().catch(() => {});
          throw error;
        }
      });
      return pendingRun;
    };
    root.AjaxCartUpdate = cart => {
      if (!validCart(cart)) return false;
      if (!cart.item_count) { declined.clear(); saveDecisions(); }
      return scheduleRefresh();
    };
    let checkoutQueued = false;
    function checkout(event) {
      event.preventDefault(); event.stopImmediatePropagation();
      // Queued behind any update in flight, so a click during "Updating bag…" is not lost.
      if (checkoutQueued) return;
      checkoutQueued = true;
      run(async () => {
        await refresh();
        const decision = drawer.querySelector('[data-hipster-gift-prompt]');
        if (decision) {
          drawer.open(); message(words.decide);
          const body = drawer.querySelector('.cart-drawer-body');
          if (body) body.scrollTop = 0;
          decision.querySelector('button')?.focus({preventScroll:true});
          return;
        }
        if (typeof root.XTND_FAST_CHECKOUT?.open === 'function') {
          await root.XTND_FAST_CHECKOUT.open();
        } else {
          // Preserve checkout if Cart Saver is unavailable.
          root.location.assign(route + 'checkout');
        }
      }).finally(() => { checkoutQueued = false; });
    }
    root.addEventListener('click', event => {
      const target = event.target.closest?.('[name="checkout"], a[href*="/checkout"], .cart-drawer-empty-footer');
      if (!target) return;
      if (target.matches('[name="checkout"], a[href*="/checkout"]')) { checkout(event); return; }
      if (target.matches('.cart-drawer-empty-footer')) drawer.close();
    },true);
    root.addEventListener('submit', event => {
      if (event.submitter?.name === 'checkout' || event.target.matches('form[action$="/checkout"]')) checkout(event);
    },true);
    // Opening via the header and opening after add-to-cart both change this class.
    let wasOpen = drawer.classList.contains('active');
    new MutationObserver(() => {
      const open = drawer.classList.contains('active');
      if (open && !wasOpen) scheduleRefresh();
      wasOpen = open;
    }).observe(drawer,{attributes:true,attributeFilter:['class']});
    root.HipsterGifts = { refresh: scheduleRefresh, rules:RULES };
    if (wasOpen || document.querySelector('.section-main-cart-container')) scheduleRefresh();
    // Load the offer catalog while the page is idle, so the first drawer open only reads the bag.
    else (root.requestIdleCallback || (fn => setTimeout(fn, 1000)))(() => { catalog().catch(() => {}); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',boot,{once:true});
  else boot();
})(typeof window !== 'undefined' ? window : globalThis);
